<template>
  <!--
    Same live-region treatment as `Toast`: an error interrupts, everything
    else waits for a pause. Alerts are usually rendered in response to a user
    action rather than arriving unprompted, so the role matters less than it
    does for a toast, but the two should not disagree about it.
  -->
  <div
    class="alert"
    :style="cssWidth"
    :class="classes"
    :role="liveRole"
    :aria-live="liveRole === 'alert' ? 'assertive' : 'polite'"
  >
    <slot name="image" />
    <div class="alert__wrapper">
      <i v-if="loading" class="alert__loading" aria-hidden="true"></i>
      <i
        v-else-if="type !== 'blank'"
        class="alert__icon"
        :class="iconClass"
        aria-hidden="true"
      ></i>

      <div class="alert__content">
        <div v-if="hasTitleSlot" class="alert__title">
          <slot name="title" />
        </div>
        <div v-if="hasDefaultSlot" class="alert__message"><slot /></div>

        <div v-if="hasActionsSlot" class="alert__actions">
          <slot name="actions" />
        </div>
      </div>

      <button
        v-if="closeButton"
        type="button"
        class="alert__close"
        :aria-label="$t('action.close')"
        @click="close"
      >
        <i class="iconoir-cancel" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    /**
     * The type of alert to display
     */
    type: {
      required: false,
      type: String,
      default: 'info-primary',
      validator: function (value) {
        return [
          'info-neutral',
          'info-primary',
          'warning',
          'error',
          'success',
          'blank',
        ].includes(value)
      },
    },
    /**
     * The position of the alert. If not set it will be display in the flow.
     */
    position: {
      required: false,
      type: String,
      default: null,
      validator: function (value) {
        return ['top', 'bottom'].includes(value)
      },
    },
    /**
     * Whether to display a loading spinner or not.
     */
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    /**
     * Whether to display a close button or not.
     */
    closeButton: {
      required: false,
      type: Boolean,
      default: false,
    },
    /**
     * The  width of the alert.
     */
    width: {
      required: false,
      type: Number,
      default: null,
    },
  },
  emits: ['close'],
  computed: {
    hasDefaultSlot() {
      return !!this.$slots.default
    },
    hasTitleSlot() {
      return !!this.$slots.title
    },
    hasActionsSlot() {
      return !!this.$slots.actions
    },
    classes() {
      const classObj = {
        [`alert--${this.type}`]: this.type,
        [`alert--${this.position}`]: this.position,
      }
      return classObj
    },
    isWarningAlert() {
      return this.type === 'warning'
    },
    isErrorAlert() {
      return this.type === 'error'
    },
    isInfoAlert() {
      return this.type === 'info-neutral' || this.type === 'info-primary'
    },
    isSuccessAlert() {
      return this.type === 'success'
    },
    liveRole() {
      return this.type === 'error' ? 'alert' : 'status'
    },
    iconClass() {
      const classObj = {
        'iconoir-warning-triangle': this.isWarningAlert,
        'iconoir-info-empty': this.isInfoAlert,
        'iconoir-check-circle': this.isSuccessAlert,
        'iconoir-warning-circle': this.isErrorAlert,
      }
      return classObj
    },
    cssWidth() {
      if (this.width)
        return {
          '--alert-width': this.width + 'px',
        }
      return 'auto'
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
  },
}
</script>
